// The module manager's rules for names, paths, file types, version numbers and
// licences (docs/MODULE_MANAGER_DESIGN.md, "What the server refuses"). The
// Modules page keeps its own copy of the path and name rules
// (web/ide/modules/src/rules.js); web/ide/tests/module_path_cases.json keeps
// the two, and the desktop hosts' checks, in step.

// Blitz keywords, which the compiler won't take as a part of a module name
// (compiler/toker.cpp), with the words it reads as keywords in context.
export const KEYWORDS = new Set(("abs after and as before case const data default delete dialect dim double each else " +
  "elseif end endif enum exit false field first float for forever function global gosub goto handle if implements " +
  "import include insert int interface last list local long map method mod module new next not null object or pi " +
  "private queue read repeat restore return sar select self set sgn shl shr stack step str super then to true type " +
  "until wend while worker xor").split(" "));

// Member names nobody may choose.
export const RESERVED_NAMES = new Set(["blitz", "blitz3d", "blitzplus", "blitzmax", "b3d", "bb", "official", "admin",
  "administrator", "moderator", "moderators", "mod", "staff", "support", "help", "system", "root", "server",
  "anthropic", "claude", "blip", "sample", "samples", "example", "examples", "test", "tests", "modules", "module",
  "me", "mine", "new", "null", "user", "users", "member", "members", "registry", "library", "std", "core"]);

const PART = /^[a-z][a-z0-9_]{1,31}$/;

// Why a member name, or a module's own part, can't be used, or null.
export function namePartProblem(part, what = "name") {
  if (typeof part !== "string" || !PART.test(part)) {
    return `A ${what} is 2 to 32 letters, digits and underscores, starting with a letter`;
  }
  if (KEYWORDS.has(part)) return `"${part}" is a Blitz keyword, so it can't be a ${what}`;
  return null;
}

export function memberNameProblem(name) {
  const problem = namePartProblem(name, "member name");
  if (problem) return problem;
  if (RESERVED_NAMES.has(name)) return `"${name}" is reserved`;
  return null;
}

// Why a module name such as simon.physics can't be used by member, or null.
export function moduleNameProblem(name, member) {
  if (typeof name !== "string") return "Give the module a name";
  const parts = name.split(".");
  if (parts.length !== 2) return `A module's name is your member name, a dot and one more part, such as ${member}.physics`;
  if (parts[0] !== member) return `Your modules' names start with your member name: ${member}.${parts[1]}`;
  return namePartProblem(parts[1], "module name");
}

// Names Windows can't make (CON, NUL, COM1 and so on, with any extension).
const DEVICE = /^(con|prn|aux|nul|com[0-9]|lpt[0-9]|conin\$|conout\$)(\..*)?$/i;

// Why a path in a module, relative to a program's modules folder, can't be
// used, or null. Paths use / between parts, stay inside the folder, and are
// names every system can make.
export function pathProblem(path) {
  if (typeof path !== "string" || !path) return "A path is missing";
  if (path.length > 200) return `"${path.slice(0, 60)}…" is longer than 200 characters`;
  if (path.startsWith("/") || path.includes("\\") || /^[A-Za-z]:/.test(path)) {
    return `"${path}" must be relative, with / between its parts`;
  }
  for (const part of path.split("/")) {
    if (!part || part === "." || part === "..") return `"${path}" has an empty, . or .. part`;
    if (/[\u0000-\u001f<>:"|?*\u007f]/.test(part)) return `"${path}" has a character Windows can't use in a name`;
    if (/[. ]$/.test(part)) return `"${path}" has a part that ends in a dot or a space`;
    if (DEVICE.test(part)) return `"${path}" uses a name Windows keeps for devices`;
  }
  return null;
}

const TEXT = new Set([".md", ".txt", ".json", ".csv"]);
const ASSETS = new Set([
  // Images and textures.
  ".png", ".jpg", ".jpeg", ".bmp", ".gif", ".tga", ".dds", ".webp", ".ktx2", ".basis", ".pcx", ".psd", ".hdr",
  // Sounds and music.
  ".wav", ".ogg", ".mp3", ".flac", ".mod", ".xm", ".s3m", ".it", ".mid", ".midi",
  // Models and animation.
  ".b3d", ".x", ".3ds", ".md2", ".gltf", ".glb", ".obj", ".mtl", ".bin", ".fbx",
  // Fonts, shaders and worlds.
  ".ttf", ".otf", ".fnt", ".b3shader", ".hlsl", ".hlsli", ".world", ".b3dpal",
]);
// Text files whose names have no extension.
const BARE = new Set(["license", "licence", "copying", "readme", "authors", "changelog", "notice"]);

// Why a file can't be part of a module, or null.
export function fileTypeProblem(path) {
  const name = path.slice(path.lastIndexOf("/") + 1).toLowerCase();
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return BARE.has(name) ? null : `"${path}" has no extension, so it can't be checked`;
  const extension = name.slice(dot);
  if (extension === ".bb" || TEXT.has(extension) || ASSETS.has(extension)) return null;
  if ([".b3m", ".dll", ".dylib", ".so", ".exe", ".decls"].includes(extension)) {
    return `"${path}": modules are source code, so they can't hold ${extension} files`;
  }
  return `"${path}": a module can't hold ${extension} files`;
}

export const isTextFile = (path) => {
  const name = path.slice(path.lastIndexOf("/") + 1).toLowerCase();
  const dot = name.lastIndexOf(".");
  return dot <= 0 ? BARE.has(name) : name.endsWith(".bb") || TEXT.has(name.slice(dot));
};

// Version numbers: MAJOR.MINOR.PATCH, each a number of up to 6 digits.
const VERSION = /^(0|[1-9][0-9]{0,5})\.(0|[1-9][0-9]{0,5})\.(0|[1-9][0-9]{0,5})$/;
export function parseVersion(text) {
  const match = typeof text === "string" && VERSION.exec(text);
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}
export function compareVersions(a, b) {
  const x = typeof a === "string" ? parseVersion(a) : a, y = typeof b === "string" ? parseVersion(b) : b;
  for (let k = 0; k < 3; ++k) if (x[k] !== y[k]) return x[k] < y[k] ? -1 : 1;
  return 0;
}

// The files that make up a module: its main file, <member>/<name>.bb, and
// everything in the folder <member>/<name>/.
export function moduleMainFile(name) {
  return name.replace(".", "/") + ".bb";
}
export function belongsToModule(path, name) {
  const base = name.replace(".", "/");
  return path === base + ".bb" || path.startsWith(base + "/");
}

// What a module's source files declare: the Module line's name, and each
// Import, Include and quoted Import with its line (from 1). Comments, after
// ;, are ignored, and strings are kept whole.
export function scanSource(text) {
  const result = { module: null, imports: [], includes: [] };
  const lines = text.split(/\r\n|\r|\n/);
  let block = false;
  for (let row = 0; row < lines.length; ++row) {
    let line = lines[row];
    // Block comments, which the Extended dialect allows on lines of their own.
    if (block) {
      if (/^\s*\*\//.test(line) || /\*\/\s*$/.test(line)) block = false;
      continue;
    }
    if (/^\s*\/\*/.test(line) && !/\*\/\s*$/.test(line.replace(/^\s*\/\*/, ""))) {
      block = true;
      continue;
    }
    // The comment, outside strings.
    let inString = false, cut = line.length;
    for (let k = 0; k < line.length; ++k) {
      if (line[k] === '"') inString = !inString;
      else if (line[k] === ";" && !inString) { cut = k; break; }
    }
    line = line.slice(0, cut).trim();
    let match;
    if ((match = /^Module\s+([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)\s*$/i.exec(line))) {
      if (!result.module) result.module = { name: match[1].toLowerCase(), row: row + 1 };
    } else if ((match = /^Import\s+"([^"]*)"/i.exec(line))) {
      result.includes.push({ path: match[1], row: row + 1, kind: "Import" });
    } else if ((match = /^Import\s+([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)/i.exec(line))) {
      result.imports.push({ name: match[1].toLowerCase(), row: row + 1 });
    } else if ((match = /^Include\s+"([^"]*)"/i.exec(line))) {
      result.includes.push({ path: match[1], row: row + 1, kind: "Include" });
    }
  }
  return result;
}

// Resolves a quoted path from a file in a module's version, as the compiler
// does: relative to the file, with \ or /. Returns the path relative to the
// modules folder, or null when it leaves it.
export function resolveRelative(from, target) {
  const parts = from.split("/").slice(0, -1);
  for (const part of target.replace(/\\/g, "/").split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") {
      if (!parts.length) return null;
      parts.pop();
    } else parts.push(part);
  }
  return parts.join("/");
}

// The licences a module can have, by SPDX identifier, and the LICENSE.txt
// the server adds when a module has no licence file of its own.
export const LICENCES = {
  Zlib: {
    name: "zlib",
    text: (holder, year) => `Copyright (c) ${year} ${holder}

This software is provided 'as-is', without any express or implied
warranty. In no event will the authors be held liable for any damages
arising from the use of this software.

Permission is granted to anyone to use this software for any purpose,
including commercial applications, and to alter it and redistribute it
freely, subject to the following restrictions:

1. The origin of this software must not be misrepresented; you must not
   claim that you wrote the original software. If you use this software
   in a product, an acknowledgment in the product documentation would be
   appreciated but is not required.
2. Altered source versions must be plainly marked as such, and must not be
   misrepresented as being the original software.
3. This notice may not be removed or altered from any source distribution.
`,
  },
  MIT: {
    name: "MIT",
    text: (holder, year) => `MIT License

Copyright (c) ${year} ${holder}

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
`,
  },
  "BSD-2-Clause": {
    name: "BSD 2-clause",
    text: (holder, year) => `BSD 2-Clause License

Copyright (c) ${year}, ${holder}

Redistribution and use in source and binary forms, with or without
modification, are permitted provided that the following conditions are met:

1. Redistributions of source code must retain the above copyright notice, this
   list of conditions and the following disclaimer.

2. Redistributions in binary form must reproduce the above copyright notice,
   this list of conditions and the following disclaimer in the documentation
   and/or other materials provided with the distribution.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
`,
  },
  "Apache-2.0": {
    name: "Apache 2.0",
    text: (holder, year) => `Copyright ${year} ${holder}

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
`,
  },
  "CC0-1.0": {
    name: "public domain (CC0)",
    text: (holder, year) => `${holder}, ${year}

To the extent possible under law, ${holder} has waived all copyright and
related or neighboring rights to this module, under the Creative Commons
CC0 1.0 Universal public domain dedication:

    https://creativecommons.org/publicdomain/zero/1.0/
`,
  },
};

// Whether a version's files hold a licence file of the module's own.
export function hasLicenceFile(paths, name) {
  const folder = name.replace(".", "/") + "/";
  return paths.some((path) => path.startsWith(folder) && !path.slice(folder.length).includes("/") &&
    /^(licen[cs]e|copying)(\.(txt|md))?$/i.test(path.slice(folder.length)));
}

// The next version a change suggests: a removed or changed public
// declaration makes a major version, an addition a minor one, and anything
// else a patch. Interfaces are lists of declaration lines.
export function suggestVersion(previous, before, after) {
  const [major, minor, patch] = parseVersion(previous);
  if (!before || !after) return `${major}.${minor}.${patch + 1}`;
  const old = new Set(before), now = new Set(after);
  if ([...old].some((line) => !now.has(line))) return `${major + 1}.0.0`;
  if ([...now].some((line) => !old.has(line))) return `${major}.${minor + 1}.0`;
  return `${major}.${minor}.${patch + 1}`;
}
